// Which revive offer (if any) a death earns — the rule, with no DOM and no ad
// SDK attached, so it can be read and tested on its own (same split as
// game/tutorial.ts vs ui/tutorial.ts).
//
// Design §2.6: the newbie free revive is the device's first run, first death.
// Design §8.1: the ad revive is at most once per run.
// Design §2.6 again, the load-bearing sentence: **免费复活与广告复活同一局内
// 二选一，不叠加** — one run gets one kind of revive, never both. A firstEver
// run therefore gets the free one and is done; every other run may be offered
// the ad one. In practice that keeps a run at ≤ 1 revive; the `MAX_REVIVES`
// check below is the sim's own ceiling, kept as a belt-and-braces guard.
// Design §4.6: 广告未就绪，不显示复活按钮 — not ready is not offered, and the
// death copy carries on exactly as it does today.

import { MAX_REVIVES } from '@beatme/sim';

export type ReviveKind = 'newbie' | 'ad';

export interface ReviveSituation {
  /** Device has never finished a run (RunContext.firstEver). */
  firstEver: boolean;
  /** The newbie offer already happened this run (taken or declined). */
  newbieReviveUsed: boolean;
  /** The ad offer already happened this run. */
  adReviveUsed: boolean;
  /** Revive events already accepted by the sim this run. */
  revives: number;
  /**
   * The `Revive` event still fits the §2.7 input budget (≤600 events per run,
   * ≤4 on any one frame). A death frame that is already full cannot carry one:
   * pushing it anyway would make the server reject the whole run with 422, so
   * the honest move is to not offer a revive we cannot record.
   */
  canRecordRevive: boolean;
  /** Server flag: ads are switched on at all. */
  adsEnabled: boolean;
  /** A rewarded ad is loaded and showable right now. */
  rewardedReady: boolean;
}

/** The offer to show on this death, or null to go straight to the result screen. */
export function reviveOffer(s: ReviveSituation): ReviveKind | null {
  if (!s.canRecordRevive) return null;
  if (s.revives >= MAX_REVIVES) return null;
  if (s.firstEver && !s.newbieReviveUsed && s.revives === 0) return 'newbie';
  // 二选一: a run that has already had its free revive never sees the ad one.
  if (s.newbieReviveUsed || s.adReviveUsed) return null;
  if (!s.adsEnabled || !s.rewardedReady) return null;
  return 'ad';
}

export { MAX_REVIVES };
